import { FieldValueViewModel } from '@vdms-hq/selectors';

export type FieldValueGraphNode = {
  uuid: string;
  label: string;
  key: string | null;
  typeName: string;
  restricted: string[];
  siblings: { key: string; value: string | string[] }[];
  isFocus: boolean;
  isInParentPath: boolean;
  isRelatedViaSibling: boolean;
  children: FieldValueGraphNode[];
};

export type FieldValueGraph = {
  root: FieldValueGraphNode;
  focusUuid: string;
  parentPath: string[];
  relatedViaSiblingUuids: string[];
};

const uniqueStrings = (values: string[]) => [...new Set(values)];
const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

export const buildFieldValuesGraph = (
  fields: FieldValueViewModel[],
  focus: FieldValueViewModel,
): FieldValueGraph | null => {
  const nodeById = new Map<string, FieldValueGraphNode>();
  const childrenByParentId = new Map<string, string[]>();

  for (const field of fields) {
    nodeById.set(field.uuid, {
      uuid: field.uuid,
      label: field.label,
      key: field.key ?? null,
      typeName: field.type.name,
      restricted: asStringArray(field.extra?.restricted),
      siblings: field.extra?.siblings ?? [],
      isFocus: field.uuid === focus.uuid,
      isInParentPath: false,
      isRelatedViaSibling: false,
      children: [],
    });
  }

  for (const field of fields) {
    const parentId = field.parent_field?.uuid;
    if (!parentId || !nodeById.has(parentId) || !nodeById.has(field.uuid)) {
      continue;
    }

    const existing = childrenByParentId.get(parentId) ?? [];
    existing.push(field.uuid);
    childrenByParentId.set(parentId, existing);
  }

  for (const [parentId, childIds] of childrenByParentId.entries()) {
    const parent = nodeById.get(parentId);
    if (!parent) {
      continue;
    }

    parent.children = childIds
      .map((childId) => nodeById.get(childId))
      .filter((child): child is FieldValueGraphNode => !!child);
  }

  const focusNode = nodeById.get(focus.uuid);
  if (!focusNode) {
    return null;
  }

  const parentPath: string[] = [];
  const visitedParents = new Set<string>();
  let currentParentId =
    fields.find((item) => item.uuid === focus.uuid)?.parent_field?.uuid ?? focus.parent_field?.uuid ?? null;

  while (currentParentId && !visitedParents.has(currentParentId)) {
    visitedParents.add(currentParentId);
    parentPath.push(currentParentId);
    const parentField = fields.find((item) => item.uuid === currentParentId);
    currentParentId = parentField?.parent_field?.uuid ?? null;
  }

  for (const id of parentPath) {
    const node = nodeById.get(id);
    if (node) {
      node.isInParentPath = true;
    }
  }

  const siblingKeys = uniqueStrings((focusNode.siblings ?? []).map((item) => item.key).filter(Boolean));
  const relatedViaSiblingUuids = uniqueStrings(
    fields
      .filter((field) => {
        const fieldKey = field.key ?? '';
        return siblingKeys.includes(fieldKey) && field.uuid !== focus.uuid;
      })
      .map((field) => field.uuid),
  );

  for (const relatedId of relatedViaSiblingUuids) {
    const node = nodeById.get(relatedId);
    if (node) {
      node.isRelatedViaSibling = true;
    }
  }

  const topParentId = parentPath[parentPath.length - 1];
  const root = (topParentId ? nodeById.get(topParentId) : null) ?? focusNode;

  return {
    root,
    focusUuid: focus.uuid,
    parentPath,
    relatedViaSiblingUuids,
  };
};
